'use client';
import { useRef } from 'react';
import { useProject } from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { isStatusApplicable } from '@witmetrics/unison-core';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import ProjectStatus from '@/components/ProjectStatus';
import Note from '@/components/Note';
import CaseTypeIcon from '@/components/CaseTypeIcon';
import { CloseIcon, OptionsIcon } from '@/icons';
import DefaultOptionsMenu from '@/components/DefaultOptionsMenu';
import IconButton from '@/components/IconButton';
import ProjectDialog from '@/dialogs/ProjectDialog';
import DeleteProjectDialog from '@/dialogs/DeleteProjectDialog';
import UserChip from '@/components/UserChip';
import ProjectTypeChip from '@/components/ProjectTypeChip';
import { classes } from './utils';

export default function CaseHeader() {
  const menuToggleRef = useRef(null);
  const { activeProjectId, onClearActiveProject, onSelectLead } = useAppState();
  const project = useProject(activeProjectId);
  const [isEditing, toggleEditing] = useToggle();
  const [isDeleting, toggleDeleting] = useToggle();
  const [isMenuOpen, toggleMenu] = useToggle();

  const handleOpenLead = () => {
    onSelectLead(project!.userId!);
    onClearActiveProject();
  };

  const handleEdit = () => {
    toggleMenu(false);
    toggleEditing(true);
  };

  const handleDelete = () => {
    toggleMenu(false);
    toggleDeleting(true);
  };

  if (!project) return null;

  const { name, user } = project;

  return (
    <>
      {isEditing && (
        <ProjectDialog
          projectId={activeProjectId!}
          onClose={() => toggleEditing(false)}
        />
      )}
      {isDeleting && (
        <DeleteProjectDialog
          projectId={activeProjectId!}
          onDelete={onClearActiveProject}
          onClose={() => toggleDeleting(false)}
        />
      )}
      <div className={classes.header}>
        <div className={classes.infoWrapper}>
          <CaseTypeIcon
            className={classes.caseType}
            projectTypeId={project.unisonProjectTypeId}
            iconClassName={classes.caseTypeIcon}
          />
          <div className={classes.info}>
            {!isEmpty(project.externalRef) && project.externalRef !== name && (
              <div className={classes.externalRef}>{project.externalRef}</div>
            )}
            <div className={classes.nameWrapper}>
              <h1 className={classes.name}>{name}</h1>
              {isStatusApplicable(project.unisonProjectTypeId) && (
                <ProjectStatus
                  className={classes.status}
                  projectId={project.id}
                  projectStatusId={project.unisonProjectStatusId}
                />
              )}
            </div>
            <div className={classes.details}>
              {project.unisonProjectTypeId && (
                <ProjectTypeChip projectTypeId={project.unisonProjectTypeId} />
              )}
              {user && (
                <button className={classes.leadButton} onClick={handleOpenLead}>
                  <UserChip user={user} />
                </button>
              )}
            </div>
            {!isEmpty(project.description) && (
              <Note
                className={classes.description}
                value={project.description}
              />
            )}
          </div>
        </div>
        <div className={classes.controls}>
          <IconButton
            ref={menuToggleRef}
            aria-label="options"
            onClick={() => toggleMenu()}>
            <OptionsIcon className={classes.optionsIcon} />
          </IconButton>
          <DefaultOptionsMenu
            anchorTo={menuToggleRef.current}
            open={isMenuOpen}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onClose={() => toggleMenu(false)}
          />
          <IconButton aria-label="close" onClick={onClearActiveProject}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
    </>
  );
}
